/**
 * Utility functions for user data management
 */

// Storage key for user data in localStorage
export const USER_STORAGE_KEY = 'wow_agent_user_data';

/**
 * Gets the user data from local storage, including the full user object
 * @returns {Object} Full user object with additional userId and accountId properties
 */
export const getUserData = () => {
  try {
    const storedUser = localStorage.getItem(USER_STORAGE_KEY);

    if (!storedUser) return { userId: null, accountId: null };

    const parsedUser = JSON.parse(storedUser);

    if (new Date().getTime() > parsedUser.expiresAt) {
      return { userId: null, accountId: null };
    }

    // Try different properties for the account ID
    let accountId = parsedUser.account_id;

    // If account_id is not available, try accountId
    if (!accountId) {
      accountId = parsedUser.accountId;
    }

    // If accountId is not available, try to find it in other properties
    if (!accountId && parsedUser.accounts && parsedUser.accounts.length > 0) {
      accountId = parsedUser.accounts[0].id;
    }

    // For impersonated users, log the accountId being used
    if (parsedUser.isImpersonating) {
      console.log('getUserData for impersonated user, accountId:', accountId);
    }

    // Return the full user object with additional userId and accountId properties
    return {
      ...parsedUser,
      userId: parsedUser.id,
      accountId
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    return { userId: null, accountId: null };
  }
};

/**
 * Gets just the user ID from local storage
 * @returns {string|null} The user ID or null if not available
 */
export const getUserId = () => {
  return getUserData().userId;
};

/**
 * Gets the appropriate account ID based on user type and context
 *
 * @deprecated Use the AccountContext instead. This function is kept for backward compatibility.
 *
 * @param {boolean} isAdmin - Whether the user is an admin
 * @param {boolean} isSuperAdmin - Whether the user is a super admin
 * @param {string|null} selectedAccountId - The selected account ID from localStorage
 * @param {string|null} overrideAccountId - An optional override account ID (takes highest precedence)
 * @deprecated This parameter was used for logging and is no longer needed
 * @returns {string|null} The appropriate account ID to use
 */
export const getAccountIdToUse = (isAdmin, isSuperAdmin, selectedAccountId, overrideAccountId = null) => {
  let accountIdToUse;
  const userData = getUserData();

  // No debugging logs needed here

  // If an override accountId is provided, always use that first
  if (overrideAccountId) {
    accountIdToUse = overrideAccountId;
  }
  // For regular admins
  else if (isAdmin && !isSuperAdmin) {
    // Use selectedAccountId if it exists, otherwise use default
    accountIdToUse = selectedAccountId || userData.accountId;
  }
  // For super admins
  else if (isSuperAdmin) {
    // Super admins can access any account, so use selectedAccountId if specified
    accountIdToUse = selectedAccountId || userData.accountId;
  }
  // For regular users
  else {
    // Regular users should always use their default accountId
    accountIdToUse = userData.accountId;
  }
  return accountIdToUse;
};
